import React, { useEffect, useMemo } from 'react';
import 'react-grid-layout/css/styles.css';
import { Layout } from 'react-grid-layout';
import RGL, { WidthProvider } from 'react-grid-layout';
import { ToastServiceProvider } from '../../components/toast/ToastService';
import { filterWidgetItemnsForTestScreen, GridLayoutItem } from '../../well-chart/WidgetLayoutService';
import Loader from '../common/Loader/Loader';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import { fetchColumnsData } from '../../dispatchers/ColumnSelectorDispatcher';
import { RootState } from '../../store';
import WellCompletionsTableWidget from './WellCompletionTableWidget';
import { getMopFromIndustryApplicationId } from '../../utilities/completionsUtils';
import { ReactComponent as WarningIcon } from '../../images/alert-triangle.svg';
import { ReactComponent as ArrowRightIcon } from '../../images/arrow-right.svg';

const GridLayout = WidthProvider(RGL);

export default function Main() {
  const dispatch = useAppDispatch();
  const wellCompletionsObject = useAppSelector((state: RootState) => state.testWellColumnsSlice.data?.widgets) || [];
  const isLoading = useAppSelector((state: RootState) => state.testWellColumnsSlice.loading);
  const industryApplicationId = useAppSelector((state: RootState) => state.AssetAnalysis.industryApplicationId);
  const wellCompletionsState = useAppSelector((state: RootState) => state.WellCompletions);
  const wellId = useAppSelector((state: RootState) => state.WellTestIdSlice?.data?.WellId);
  const selectedAssetId = useAppSelector((state) => state.assetGroups?.selectedAssetId);

  const layout = 'wellCompletions';

  const completionWarnings = wellCompletionsState.completionWarnings.data || [];
  const loadingWarnings = wellCompletionsState.completionWarnings.loading;

  useEffect(() => {
    const fetchData = async () => {
      const id = typeof industryApplicationId === 'number' ? industryApplicationId : 0;
      const mop = getMopFromIndustryApplicationId(id);
      if (mop) {
        await dispatch(fetchColumnsData(`${mop}-completions`));
      }
    };
    fetchData();
  }, [dispatch, industryApplicationId]);

  const safeLayout = (wellCompletionsObject: any[], layout: string): Layout[] => {
    wellCompletionsObject = filterWidgetItemnsForTestScreen(wellCompletionsObject, layout);
    return wellCompletionsObject
      ?.map((obj: any, index: number): GridLayoutItem | null => {
        const lg = obj?.layout?.lg;
        if (!lg || !obj?.key) {
          console.warn(`Invalid layout at index ${index}`, obj);
          return null;
        }

        const item: GridLayoutItem = {
          i: String(obj.key),
          x: lg.x,
          y: lg.y,
          w: lg.width,
          h: lg.height,
          minH: lg.minH ?? 1,
          minW: lg.minW ?? 6,
          moduleId: obj.widgetProperty?.moduleId,
        };

        return item;
      })
      .filter((item): item is GridLayoutItem => item !== null);
  };

  const layoutMemo = useMemo(() => safeLayout(wellCompletionsObject, layout), [wellCompletionsObject, layout]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <ToastServiceProvider>
      <div className='well-completions-container'>
        {/* Completion Warnings */}
        {!loadingWarnings && completionWarnings && completionWarnings.length > 0 && (
          <div className='completion-warnings'>
            <div className='warnings-header'>
              <WarningIcon className='warning-icon' />
              <div className='warnings-header-text-and-list'>
                <div className='warnings-header-row'>
                  <span className='warnings-header-text'>Just to let you know this might be a problem</span>
                </div>
                {!wellId ? (
                  <div className='warnings-content'>
                    <div className='warning-item'>
                      This well is not onboarded into pump checker correctly - {selectedAssetId ?? ''}. Contact your
                      administrator to resolve this.
                    </div>
                  </div>
                ) : (
                  <>
                    <div className='warnings-content'>
                      {completionWarnings.map((warning: string, index: number) => (
                        <div key={index} className='warning-item'>
                          {warning}
                        </div>
                      ))}
                    </div>
                    <div className='warnings-header-actions' style={{ opacity: 0.4, cursor: 'not-allowed' }}>
                      <button className='warning-link' type='button' style={{ cursor: 'not-allowed' }}>
                        Learn more
                      </button>
                      <button className='warning-link' type='button' style={{ cursor: 'not-allowed' }}>
                        View changes
                      </button>
                      <ArrowRightIcon className='arrow-right-icon' />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        <GridLayout
          layout={layoutMemo}
          cols={12}
          rowHeight={30}
          isDraggable={false}
          isResizable={true}
          className='completions-layout-react-grid'
          useCSSTransforms={false}
          isDroppable={false}
        >
          {layoutMemo?.map((item: any) => (
            <div key={item.i} id={`grid-item-${item.i}`} className='grid-item'>
              {(item.i === 'esp-completions-table' ||
                item.i === 'gl-completions-table' ||
                item.i === 'gapl-completions-table') && <WellCompletionsTableWidget />}
            </div>
          ))}
        </GridLayout>
      </div>
    </ToastServiceProvider>
  );
}
